/**
 * submitWarnings.ts
 * Shared pre-commit warning formatter — SubmitCheckResult → confirm-dialog body.
 *
 * G2-1 (scheduling audit #2): extracted VERBATIM from ScenarioHeaderChrome so
 * every commit door renders the SAME advisory bullets:
 *   - Scenario Planning "Finalize Schedule" (GET /scenarios/:key/submit-check)
 *   - Quote editor / WO detail "Commit Schedule" (GET /schedule/commit-check)
 *   - Master Gantt bulk commit (POST /schedule/commit-bulk-check)
 *
 * Returns null when the check is clean (callers show their plain confirm
 * message). Each category shows a count + up to three examples. Advisory
 * only — the dialogs stay confirmable ("… anyway").
 *
 * `clean` is computed SERVER-side (single source of truth) and, since Wave-3
 * R2, folds in the non-blocking `weekOverloaded` advisory — its bullet renders
 * here so an overloaded-only plan never opens an empty warn dialog.
 */

import type { SubmitCheckResult } from '../../services/scheduleScenario';
import { weekOfMondayLabel } from './employeeScheduleShared';

export function buildSubmitWarnings(check: SubmitCheckResult): string | null {
  if (check.clean) return null;
  const { unassignedTasks, overbookedWeeks, hardConflicts, crossScenarioHolds, unmappedTests, weekOverloaded } = check;
  const plural = (n: number) => (n !== 1 ? 's' : '');
  const more = (n: number, shown: number) => (n > shown ? '; …' : '');
  const lines: string[] = [];
  /** 'YYYY-MM-DD' → 'Jul 14' (short day label for conflict days). */
  const fmtDay = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  // G-4 (audit 2026-07-01) — FIRST bullet, above hard conflicts: these tests are
  // MISSING from the plan entirely (no catalog mapping → generation skipped
  // them). Advisory like the rest — the user can still submit after confirming.
  const unmappedCount = unmappedTests?.length ?? 0;
  if (unmappedTests && unmappedCount > 0) {
    const ex = unmappedTests.slice(0, 3);
    lines.push(
      `• ${unmappedCount} test${plural(unmappedCount)} from the quote/work order ` +
        `${unmappedCount !== 1 ? 'are' : 'is'} NOT in this plan — no catalog mapping ` +
        `(${ex.join('; ')}${more(unmappedCount, 3)})`,
    );
  }
  if (hardConflicts.length > 0) {
    const ex = hardConflicts.slice(0, 3).map(
      (c) => `${c.machineCode} ${c.overlapStart}${c.overlapEnd !== c.overlapStart ? `–${c.overlapEnd}` : ''}`,
    );
    lines.push(
      `• ${hardConflicts.length} hard machine conflict${plural(hardConflicts.length)} ` +
        `(${ex.join('; ')}${more(hardConflicts.length, 3)})`,
    );
  }
  // Wave 3 (Q3B): `overbookedWeeks` now means task-on-absence-day CONFLICTS
  // (legacy field name kept) — the one remaining hard staffing signal.
  if (overbookedWeeks.length > 0) {
    const ex = overbookedWeeks.slice(0, 3).map((w) => {
      const days = (w.conflictDays ?? []).slice(0, 3).map(fmtDay).join(', ');
      return `${w.displayName}: assigned on a day off${days ? ` — ${days}` : ` (week of ${weekOfMondayLabel(w.weekStart)})`}`;
    });
    lines.push(
      `• ${overbookedWeeks.length} day-off conflict${plural(overbookedWeeks.length)} ` +
        `(${ex.join('; ')}${more(overbookedWeeks.length, 3)})`,
    );
  }
  // Allocation-based overload advisory (2026-08-05): Σ allocation % across
  // sources > 100 — the SAME number the Team-capacity grid shows, so this
  // bullet and the grid can never disagree. Non-blocking; folds into `clean`.
  if (weekOverloaded && weekOverloaded.length > 0) {
    const ex = weekOverloaded.slice(0, 3).map(
      (w) => `${w.displayName}: allocated ${w.allocationPercent}% of their week across jobs (week of ${weekOfMondayLabel(w.weekStart)})`,
    );
    lines.push(
      `• ${weekOverloaded.length} overloaded employee-week${plural(weekOverloaded.length)} ` +
        `(${ex.join('; ')}${more(weekOverloaded.length, 3)})`,
    );
  }
  if (unassignedTasks.length > 0) {
    const ex = unassignedTasks.slice(0, 3).map((t) => t.taskName);
    lines.push(
      `• ${unassignedTasks.length} task${plural(unassignedTasks.length)} with no employee assigned ` +
        `(${ex.join('; ')}${more(unassignedTasks.length, 3)})`,
    );
  }
  // Soft / advisory — these people are also penciled into OTHER draft plans. Committing
  // makes this plan's claim real, so the other plan(s) may need re-planning. Not a blocker.
  if (crossScenarioHolds && crossScenarioHolds.length > 0) {
    const ex = crossScenarioHolds.slice(0, 3).map((h) => {
      const plan = h.sources[0]
        ? [h.sources[0].code, h.sources[0].name].filter(Boolean).join(' ')
        : 'another plan';
      // P2-6: presence language — "held N day(s)", never the retired stored %.
      const days = h.heldDays > 0 ? ` (held ${h.heldDays} day${h.heldDays !== 1 ? 's' : ''})` : '';
      return `${h.displayName} → ${plan}${h.sources.length > 1 ? ' …' : ''}${days}`;
    });
    lines.push(
      `• ${crossScenarioHolds.length} person${plural(crossScenarioHolds.length)} also held in other draft plans ` +
        `(soft — ${ex.join('; ')}${more(crossScenarioHolds.length, 3)})`,
    );
  }

  return lines.join('\n');
}
